/**
 * RBAC types for the admin app.
 *
 * The real backend emits six Sprint 17 roles. The local/mock concierge flow still
 * uses the older `team_lead` / `advisor` aliases, so keep them as legacy roles.
 */

export type AdminRole =
  | "super_admin"
  | "ops"
  | "finance"
  | "moderator"
  | "support_lead"
  | "support";

export type LegacyRole = "team_lead" | "advisor";

export type Role = AdminRole | LegacyRole;

export const ROLE_RANK: Record<Role, number> = {
  super_admin: 100,
  ops: 80,
  finance: 70,
  moderator: 60,
  support_lead: 50,
  team_lead: 50,
  support: 10,
  advisor: 10,
};

export interface AdvisorProfile {
  /** ngôn ngữ phục vụ — auto-routing theo locale của khách */
  languages: string[];          // ['en','vi','zh']
  /** thị trường phụ trách */
  markets: string[];            // ['US','SG','VN','HK']
  /** chứng chỉ tài chính đa quốc gia */
  certifications: string[];     // ['CFA L2','Series 7','CMT L1']
  /** chuyên môn → ưu tiên gán ticket */
  specialties: string[];        // ['equity','forex','crypto','options','tax']
  title?: string;               // 'Senior Investment Advisor'
  bio?: string;
  timezone?: string;            // 'Asia/Ho_Chi_Minh'
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  /** team_lead id phụ trách */
  supervisor_id: string | null;
  timezone: string;
  is_active: boolean;
}

export interface AdminUser {
  /** BE int64 → coerce sang string khi store. */
  id: string;
  /** BE login identifier (Sprint 17). FE legacy dùng email — giữ cả 2. */
  username?: string;
  email: string;
  display_name: string;
  role: Role;
  /** null với super_admin (xuyên team); chưa có ở BE Sprint 17 — sẽ wire sau khi tách team. */
  team_id: string | null;
  /** team_lead.id; null nếu top of pyramid. */
  manager_id: string | null;
  /** chỉ team_lead + advisor có profile tư vấn (FE mock; BE serve qua `expertise`/`languages`). */
  advisor_profile?: AdvisorProfile;
  is_active: boolean;
  is_online?: boolean;
  avatar_url?: string;
  /** BE permission keys (vd "users.read", "pricing.write") — không dùng RBAC matrix FE. */
  permissions?: string[];
  /** BE Sprint 17 advisor metadata. */
  expertise?: string[];
  languages?: string[];
  mfa_enrolled?: boolean;
  must_change_password?: boolean;
}

// ── Actions để check bằng can(role, action) ──

export type Action =
  // navigation
  | "nav.users"
  | "nav.earlyAccess"
  | "nav.community"
  | "nav.moderation"
  | "nav.advisor"
  | "nav.announcements"
  | "nav.revenue"
  | "nav.pricing"
  | "nav.plans"
  | "nav.subscriptions"
  | "nav.trading"
  | "nav.pool"
  | "nav.datasources"
  | "nav.registry"
  | "nav.apiOps"
  | "nav.ai"
  | "nav.monitor"
  | "nav.system"
  | "nav.featureFlags"
  | "nav.integrations"
  // advisor workbench
  | "advisor.viewAll"            // xem conversation của mọi team
  | "advisor.viewTeam"           // xem conversation cùng team (kể cả advisor khác)
  | "advisor.assignSelf"
  | "advisor.transferIntraTeam"  // chuyển giữa advisor cùng team
  | "advisor.transferCrossTeam"
  | "advisor.escalate"
  | "advisor.reply"
  | "advisor.settings"
  | "advisor.agents"
  // admin management
  | "admin.create"
  | "admin.editAny"
  | "admin.editTeamMember"       // team_lead sửa profile advisor trong team
  | "admin.deactivate"
  | "team.create"
  | "team.editAny";
